import InputAdornment from "@mui/material/InputAdornment";
import type { InputAdornmentProps } from "@mui/material/InputAdornment";
import type { OutlinedInputProps } from "@mui/material/OutlinedInput";
import { DatePickerProps } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useSettings } from "@sinch/core/providers/AppSettings";
import { MdsCalendarTodayXs, MdsCloseXs } from "@sinch/icons";
import type { OverwriteProps } from "@sinch/types/utility.types";
import { toLuxon } from "@sinch/utils/dateTime/toLuxon";

import { DateTime } from "luxon";
import React, { ElementType, FC, forwardRef, useState } from "react";
import { MdsIcon } from "../../MdsIcon/MdsIcon";

type Props = OverwriteProps<
  DatePickerProps<DateTime>,
  {
    value?: Date | null;
    /**
     * Maxium date user can select
     */
    maxDate?: Date | null;
    /**
     * Minimum date user can select
     */
    minDate?: Date | null;
    /**
     * Add clear input icon button
     */
    clearable?: boolean;
    /**
     * Lift the picker popper above the MUI Dialog stacking context. Needed
     * when the DateInput is inside a Dialog so the calendar is not hidden
     * behind it. Name preserved for backwards compatibility with existing
     * call sites; the popper still portals to body, just with z-index
     * `theme.zIndex.modal + 1`.
     */
    disablePortal?: boolean;
    onChange: (value: Date | null) => void;
  } & Pick<OutlinedInputProps, "error" | "required" | "fullWidth" | "inputProps" | "size" | "onBlur" | "onFocus">
>;

const isValidDateOrNull = (date: Date | undefined | null) => (date && date.toString() !== "Invalid Date" ? date : null);

export const DateInputLuxonAdapter: FC<Props> = ({
  value,
  minDate,
  maxDate,
  onChange,
  error,
  onBlur,
  onFocus,
  disablePortal,
  ...props
}) => {
  const { timeZone } = useSettings();
  const [open, setOpen] = useState(false);

  const toZone = (date: DateTime | undefined) => date?.setZone(timeZone);

  return (
    <DatePicker
      {...props}
      closeOnSelect
      maxDate={maxDate ? toZone(toLuxon(maxDate)) : undefined}
      minDate={minDate ? toZone(toLuxon(minDate)) : undefined}
      onChange={(val) => onChange(val && val.isValid ? val.toJSDate() : null)}
      onClose={() => !props.readOnly && setOpen(false)}
      onOpen={() => !props.readOnly && setOpen(true)}
      open={open}
      slotProps={{
        // When mounted inside a MUI Dialog, the picker popper portals to body
        // alongside the dialog and ties on `theme.zIndex.modal`, which makes
        // the calendar sit behind the dialog. Disabling the portal so the
        // popper inherits the dialog's stacking context used to be the fix,
        // but DataGrid edit-mode cells have `overflow: hidden` so an inline
        // popper gets clipped to the row. Portal to body and lift the z-index
        // above the modal instead - both gripes solved.
        ...(disablePortal
          ? { popper: { sx: { zIndex: (theme) => theme.zIndex.modal + 1 } } }
          : {}),
        switchViewButton: { ...props.inputProps?.switchViewButton },
        field: {
          clearable: props.clearable,
        },
        textField: {
          size: props.size,
          error: error,
          onClick: () => !props.readOnly && setOpen(!open),
          InputProps: {
            startAdornment: (
              <InputAdornment position="start">
                <MdsIcon fontSize="xs" icon={MdsCalendarTodayXs} />
              </InputAdornment>
            ),
          },
          onBlur: onBlur,
          onFocus: onFocus,
          inputProps: { ...props.inputProps?.dateInput },
        },
      }}
      slots={{
        clearButton: (clearButtonProps) => (
          <MdsIcon {...clearButtonProps} fontSize="xs" icon={MdsCloseXs} sx={{ cursor: "pointer" }} />
        ),
        inputAdornment: DatePickerAdornment as ElementType<InputAdornmentProps>,
      }}
      value={toZone(toLuxon(isValidDateOrNull(value)) ?? undefined) ?? null}
    />
  );
};
export type DateInputLuxonAdapterProps = Props;

// oxlint-disable-next-line @typescript-eslint/no-unused-vars
const DatePickerAdornment = forwardRef<HTMLDivElement, InputAdornmentProps>(({ children, ...props }, ref) => (
  // This is hack to make date picker open faster, otherwise it has some issues, don't know why
  <InputAdornment {...props} ref={ref} style={{ width: 0, margin: 0, padding: 0 }} />
));
DatePickerAdornment.displayName = "DatePickerAdornment";
